import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const VALID_TYPES = ['signup', 'magiclink', 'recovery', 'email_change', 'invite', 'reauthentication'] as const;
type ValidType = typeof VALID_TYPES[number];

function isValidType(v: string | null): v is ValidType {
  return v !== null && (VALID_TYPES as readonly string[]).includes(v);
}

export default function Confirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'error'>('verifying');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const token_hash = params.get('token_hash');
    const type = params.get('type');
    const redirectTo = params.get('redirect_to');

    if (!token_hash || !isValidType(type)) {
      setStatus('error');
      setErrorMsg('Enlace inválido o caducado.');
      return;
    }

    supabase.auth.verifyOtp({ token_hash, type }).then(({ error }) => {
      if (error) {
        setStatus('error');
        setErrorMsg(error.message);
        return;
      }
      // El AuthProvider detecta el nuevo session via onAuthStateChange y
      // re-renderiza con la app autenticada. Sólo navegamos al destino.
      const safe = redirectTo && redirectTo.startsWith('/') ? redirectTo : '/bandeja';
      // Si redirectTo es absoluto (https://...), extraer su pathname.
      const finalTarget = redirectTo && /^https?:\/\//.test(redirectTo)
        ? new URL(redirectTo).pathname + new URL(redirectTo).search
        : safe;
      navigate(finalTarget, { replace: true });
    });
  }, [params, navigate]);

  if (status === 'verifying') {
    return (
      <div style={styles.screen}>
        <p style={styles.muted}>Verificando…</p>
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <div style={styles.card}>
        <h1 style={styles.title}>No se pudo verificar</h1>
        <p style={styles.error}>{errorMsg}</p>
        <button type="button" onClick={() => navigate('/', { replace: true })} style={styles.btn}>
          Volver al inicio
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  screen: {
    minHeight: '100vh',
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Söhne', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    padding: 24,
  },
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    padding: '40px 36px',
    width: 380,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
    textAlign: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
  },
  error: {
    color: '#dc2626',
    fontSize: 14,
    margin: 0,
  },
  muted: {
    color: '#64748b',
    fontSize: 14,
  },
  btn: {
    background: '#0f172a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '11px 0',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 6,
  },
};
